"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { composerAutorisation, concerne, valider } from "@/lib/tools/hostingAuthorisation";
import { dateEnToutesLettres, documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * L'attestation d'hébergement et autorisation de domiciliation.
 *
 * UNE SIGNATURE, MAIS PAS CELLE DE LA VISITE. Les deux autres formulaires
 * produisent un document que l'utilisateur signe lui-même, et leur pavé reprend
 * la signature gardée le temps de la visite. Celui-ci est signé par la personne
 * qui héberge : le pavé est donc alimenté par un état LOCAL, jamais par
 * `useSignature`. Il ne lit pas le stockage de session et n'y écrit pas, sinon
 * la signature de l'entrepreneur atterrirait au bas d'une attestation qu'un
 * tiers est censé avoir écrite, ou pire, celle de l'hébergeant repartirait sur
 * les documents suivants de l'entrepreneur.
 *
 * Elle reste facultative : l'hébergeant peut aussi signer à la main sur le
 * papier imprimé, et l'écran le dit.
 *
 * Le parcours trie d'abord, comme celui du conjoint : quelqu'un qui domicilie
 * chez lui ou chez un prestataire n'a pas besoin de ce papier, et repart avec
 * la liste des pièces qui, elles, lui seront demandées.
 */

const L = {
    fr: {
        phases: ["La situation", "Les deux personnes", "L'hébergement"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le document s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        lieuTitre: "Où sera l'adresse de votre entreprise ?",
        lieuAide: "C'est le siège que vous déclarerez au guichet unique, pas forcément l'endroit où vous travaillez.",
        chezProche: "Chez un proche qui m'héberge",
        chezProcheDetail: "Parents, ami, conjoint propriétaire",
        chezMoi: "À mon propre domicile",
        chezMoiDetail: "Que je sois propriétaire ou locataire",
        prestataire: "Ailleurs",
        prestataireDetail: "Société de domiciliation, coworking, local",
        horsSujetTitre: "Ce document ne vous est pas demandé",
        motifChezMoi: "Vous pouvez domicilier votre entreprise à votre adresse personnelle, et personne n'a à vous y autoriser. Votre justificatif de domiciliation sera une facture d'électricité, d'eau ou de téléphone, ou votre avis de taxe foncière, à votre nom et à cette adresse.",
        motifPrestataire: "Le contrat que vous a remis la société de domiciliation, l'espace de coworking ou la pépinière tient lieu de justificatif. C'est lui que vous joindrez à votre dossier, et il porte déjà les signatures nécessaires.",
        vigilanceTitre: "À vérifier quand même",
        vigilanceChezMoi: "Votre bail, le règlement de copropriété ou les règles d'urbanisme peuvent interdire d'exercer une activité chez vous, ou d'y recevoir des clients, alors que la simple domiciliation reste possible. Relisez-les avant de recevoir votre premier client à domicile.",
        vigilancePrestataire: "Une société de domiciliation doit être agréée par la préfecture pour vous fournir une adresse. Vérifiez que le contrat mentionne cet agrément : sans lui, votre immatriculation peut être refusée.",
        vousTitre: "Qui êtes-vous ?",
        vousAide: "Vous êtes la personne hébergée. C'est votre entreprise qui sera domiciliée à cette adresse.",
        hoteTitre: "Qui vous héberge ?",
        hoteAide: "La personne qui occupe le logement et qui signera l'attestation. Reprenez son état civil exact.",
        madame: "Madame",
        monsieur: "Monsieur",
        champPrenoms: "Prénoms",
        champNom: "Nom",
        champDateDeNaissance: "Date de naissance",
        champVilleDeNaissance: "Commune de naissance",
        champPaysDeNaissance: "Pays de naissance",
        aidePaysDeNaissance: "À remplir seulement si la naissance a eu lieu hors de France.",
        adresseTitre: "Où, et depuis quand ?",
        adresseAide: "L'adresse du logement, et la date à laquelle vous vous y êtes installé.",
        champVoie: "Numéro et voie",
        champComplement: "Complément d'adresse",
        aideComplement: "Facultatif. Bâtiment, étage, lieu-dit.",
        champCodePostal: "Code postal",
        champCommune: "Commune",
        champDepuisLe: "Hébergé depuis le",
        aideDepuisLe: "Une date passée. En cas de doute, la date de votre emménagement.",
        signatureTitre: "Où et quand sera-t-elle signée ?",
        signatureAide: "C'est votre hébergeant qui signe, pas vous. Il peut le faire ici, ou à la main sur le papier une fois imprimé.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        signatureHote: "Signature de votre hébergeant",
        signatureHoteAide: "Facultative, et ce n'est pas la vôtre : faites tracer ici la signature de la personne qui vous héberge, ou laissez l'emplacement vide pour qu'elle signe à la main après impression. Elle ne sera pas reprise par vos autres documents.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir mon document",
        resultatTitre: "Votre attestation",
        apercu: "Aperçu du document",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurDateFuture: "Cette date est dans le futur.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        motifFormat: "le format n'est pas reconnu",
        motifAgeImpossible: "l'année saisie donne un âge impossible, vérifiez-la",
        motifFutur: "cette date est dans le futur",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre document.",
        alerteMemePersonne: "L'hébergeant et la personne hébergée portent le même nom. Si ce n'est pas une erreur de saisie, vérifiez : on ne peut pas s'héberger soi-même.",
        suiteTitre: "Et maintenant",
        suite1: "Faites signer le document par votre hébergeant, ici ou à la main après impression.",
        suite2: "Joignez-y une copie de son justificatif de domicile à son nom, daté de moins de trois mois, sans quoi l'attestation seule ne suffit pas.",
        suite3: "Déposez les deux ensemble dans votre formalité sur le guichet unique.",
        fondTitre: "Ce que l'attestation ne dit pas",
        fond: "Elle autorise la domiciliation, c'est-à-dire l'adresse administrative de votre entreprise et son courrier. Elle ne dit rien de l'exercice de votre activité à cette adresse, qui dépend du bail, du règlement de copropriété et, dans les grandes villes, du code de la construction.",
        docEnFrancais: "",
    },
    en: {
        phases: ["The situation", "The two people", "The arrangement"],
        retour: "Back",
        prive: "Everything happens on your device: the document is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        lieuTitre: "Where will your business address be?",
        lieuAide: "This is the registered office you declare to the single window, not necessarily where you work.",
        chezProche: "With someone who hosts me",
        chezProcheDetail: "Parents, a friend, a partner who owns the place",
        chezMoi: "At my own home",
        chezMoiDetail: "Whether I own or rent it",
        prestataire: "Somewhere else",
        prestataireDetail: "Domiciliation company, coworking, premises",
        horsSujetTitre: "This document is not asked of you",
        motifChezMoi: "You may register your business at your own home address, and nobody has to authorise it. Your proof of address will be an electricity, water or telephone bill, or your property tax notice, in your name at that address.",
        motifPrestataire: "The contract given to you by the domiciliation company, the coworking space or the business incubator is your proof of address. That is what you attach to your file, and it already carries the necessary signatures.",
        vigilanceTitre: "Worth checking anyway",
        vigilanceChezMoi: "Your lease, the building rules or planning regulations may forbid carrying out an activity at home, or receiving clients, while plain registration remains possible. Read them before your first client visit.",
        vigilancePrestataire: "A domiciliation company must be approved by the prefecture to provide you with an address. Check that the contract mentions that approval: without it, your registration can be refused.",
        vousTitre: "Who are you?",
        vousAide: "You are the person being hosted. It is your business that will be registered at this address.",
        hoteTitre: "Who hosts you?",
        hoteAide: "The person who lives in the home and who will sign the attestation. Use their exact civil status.",
        madame: "Ms",
        monsieur: "Mr",
        champPrenoms: "Forenames",
        champNom: "Surname",
        champDateDeNaissance: "Date of birth",
        champVilleDeNaissance: "Town of birth",
        champPaysDeNaissance: "Country of birth",
        aidePaysDeNaissance: "Only needed if the birth took place outside France.",
        adresseTitre: "Where, and since when?",
        adresseAide: "The address of the home, and the date you moved in.",
        champVoie: "Number and street",
        champComplement: "Address line 2",
        aideComplement: "Optional. Building, floor, locality.",
        champCodePostal: "Postcode",
        champCommune: "Town",
        champDepuisLe: "Hosted since",
        aideDepuisLe: "A date in the past. If in doubt, the date you moved in.",
        signatureTitre: "Where and when will it be signed?",
        signatureAide: "Your host signs it, not you. They can do it here, or by hand on the printed paper.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        signatureHote: "Your host's signature",
        signatureHoteAide: "Optional, and it is not yours: have the person hosting you draw their signature here, or leave the space empty for them to sign by hand after printing. It will not be reused by your other documents.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See my document",
        resultatTitre: "Your attestation",
        apercu: "Document preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        erreurDateFuture: "That date is in the future.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        motifFormat: "the format is not recognised",
        motifAgeImpossible: "the year gives an impossible age, please check it",
        motifFutur: "that date is in the future",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your document.",
        alerteMemePersonne: "The host and the hosted person have the same name. If that is not a typing error, check it: nobody can host themselves.",
        suiteTitre: "What happens next",
        suite1: "Have your host sign the document, here or by hand after printing.",
        suite2: "Attach a copy of their proof of address in their name, less than three months old, without which the attestation alone is not enough.",
        suite3: "File the two together in your formality on the single window.",
        fondTitre: "What the attestation does not say",
        fond: "It authorises registration, meaning your company's administrative address and its post. It says nothing about carrying out your activity there, which depends on the lease, the building rules and, in large cities, the construction code.",
        docEnFrancais: "The document below stays in French: it is filed with a French authority, which would refuse a translated version.",
    },
};

const PHASES_PAR_ECRAN = {
    lieu: 0,
    horsSujet: 0,
    vous: 1,
    hote: 1,
    adresse: 2,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    lieu: "",
    civilite: "",
    prenoms: "",
    nom: "",
    dateDeNaissance: "",
    villeDeNaissance: "",
    paysDeNaissance: "",
    hoteCivilite: "",
    hotePrenoms: "",
    hoteNom: "",
    hoteDateDeNaissance: "",
    hoteVilleDeNaissance: "",
    hotePaysDeNaissance: "",
    voie: "",
    complement: "",
    codePostal: "",
    commune: "",
    depuisLe: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Les libellés des champs manquants, hors du dictionnaire lu par le contrôle
 *  de parité : ce sont des clés de la lib, pas des libellés du JSX. */
const LIBELLES_MANQUES = {
    fr: {
        civilite: "Votre civilité",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        dateDeNaissance: "Votre date de naissance",
        villeDeNaissance: "Votre commune de naissance",
        hoteCivilite: "La civilité de votre hébergeant",
        hotePrenoms: "Les prénoms de votre hébergeant",
        hoteNom: "Le nom de votre hébergeant",
        hoteDateDeNaissance: "La date de naissance de votre hébergeant",
        hoteVilleDeNaissance: "La commune de naissance de votre hébergeant",
        voie: "L'adresse du logement",
        codePostal: "Le code postal, en cinq chiffres",
        commune: "La commune",
        depuisLe: "La date de début de l'hébergement",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
        lieu: "Le lieu de domiciliation",
    },
    en: {
        civilite: "Your title",
        prenoms: "Your forenames",
        nom: "Your surname",
        dateDeNaissance: "Your date of birth",
        villeDeNaissance: "Your town of birth",
        hoteCivilite: "Your host's title",
        hotePrenoms: "Your host's forenames",
        hoteNom: "Your host's surname",
        hoteDateDeNaissance: "Your host's date of birth",
        hoteVilleDeNaissance: "Your host's town of birth",
        voie: "The address of the home",
        codePostal: "The postcode, five digits",
        commune: "The town",
        depuisLe: "The date the arrangement began",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
        lieu: "The registered address",
    },
};

export default function AutorisationDomiciliation({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    // L'identité gardée sur l'appareil préremplit l'écran « Qui êtes-vous ? »,
    // que le lecteur relit : rien n'est sauté. L'hébergeant reste à saisir.
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    /* Local, et jamais le store partagé : voir la note en tête de fichier. */
    const [signature, setSignature] = useState("");

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "lieu",
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR({ ...VIDE, ...identite });
            setCopie(false);
            setSignature("");
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    // Seule la personne hébergée est proposée à la garde, jamais l'hébergeant
    // ni son adresse, une fois l'écran « Qui êtes-vous ? » passé.
    useSignalerIdentite(
        {
            civilite: r.civilite,
            prenoms: r.prenoms,
            nom: r.nom,
            dateDeNaissance: r.dateDeNaissance,
            villeDeNaissance: r.villeDeNaissance,
            paysDeNaissance: r.paysDeNaissance,
        },
        etape === "hote" || etape === "adresse" || etape === "signature" || etape === "resultat",
    );

    // La date du jour vient de la page, jamais d'un new Date() ici : lue des
    // deux côtés, elle produirait un écart d'hydratation.
    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const declaration = useMemo(
        () => composerAutorisation(reponses, aujourdhui),
        [reponses, aujourdhui],
    );
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    /* Un champ rempli mais refusé ne se dit pas « il manque » : le lecteur
       relirait dix fois ce qu'il a déjà écrit. */
    const motifLisible = (motif) => {
        if (motif === "futur") return t.motifFutur;
        if (motif === "ageImpossible") return t.motifAgeImpossible;
        return t.motifFormat;
    };


    /* Sous le champ, le refus se dit en phrase, et surtout il MONTRE la date
       comprise : « nous lisons 3 mai 0019 » est plus parlant que « année
       invalide » devant un champ où l'on croit avoir tapé 1962. */
    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "futur") return t.erreurDateFuture;
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: dateEnToutesLettres(r[champ]) });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const motif = concerne(r).motif;

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier("attestation-hebergement-domiciliation", {
                prenoms: r.hotePrenoms,
                nom: r.hoteNom,
            }),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé par le navigateur : le bouton ne ment pas,
            // il ne se passe rien et le PDF reste la voie normale.
        }
    };

    /** Les cinq champs d'une identité, communs aux deux personnes. */
    const identiteDe = (prefixe) => (
        <div className="space-y-5">
            <Segments
                options={[
                    { cle: "madame", libelle: t.madame },
                    { cle: "monsieur", libelle: t.monsieur },
                ]}
                valeur={r[prefixe ? "hoteCivilite" : "civilite"]}
                poser={poser(prefixe ? "hoteCivilite" : "civilite")}
            />
            <div className="grid sm:grid-cols-2 gap-4">
                <Champ
                    libelle={t.champPrenoms}
                    valeur={r[prefixe ? "hotePrenoms" : "prenoms"]}
                    poser={poser(prefixe ? "hotePrenoms" : "prenoms")}
                />
                <Champ
                    libelle={t.champNom}
                    valeur={r[prefixe ? "hoteNom" : "nom"]}
                    poser={poser(prefixe ? "hoteNom" : "nom")}
                />
            </div>
            <Champ
                type="date"
                libelle={t.champDateDeNaissance}
                valeur={r[prefixe ? "hoteDateDeNaissance" : "dateDeNaissance"]}
                poser={poser(prefixe ? "hoteDateDeNaissance" : "dateDeNaissance")}
                erreur={erreurDe(prefixe ? "hoteDateDeNaissance" : "dateDeNaissance")}
            />
            <div className="grid sm:grid-cols-2 gap-4">
                <Champ
                    libelle={t.champVilleDeNaissance}
                    valeur={r[prefixe ? "hoteVilleDeNaissance" : "villeDeNaissance"]}
                    poser={poser(prefixe ? "hoteVilleDeNaissance" : "villeDeNaissance")}
                />
                <Champ
                    libelle={t.champPaysDeNaissance}
                    aide={t.aidePaysDeNaissance}
                    valeur={r[prefixe ? "hotePaysDeNaissance" : "paysDeNaissance"]}
                    poser={poser(prefixe ? "hotePaysDeNaissance" : "paysDeNaissance")}
                />
            </div>
        </div>
    );

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "lieu") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.lieuTitre} aide={t.lieuAide}>
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { cle: "proche", icone: "users-group-rounded-linear", titre: t.chezProche, detail: t.chezProcheDetail },
                        { cle: "chezMoi", icone: "user-rounded-linear", titre: t.chezMoi, detail: t.chezMoiDetail },
                        { cle: "prestataire", icone: "clipboard-check-linear", titre: t.prestataire, detail: t.prestataireDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, lieu: c.cle }));
                                aller(c.cle === "proche" ? "vous" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {motif === "chezMoi" ? t.motifChezMoi : t.motifPrestataire}
                    </p>

                    <div className="mt-6 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="info-circle-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.vigilanceTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {motif === "chezMoi" ? t.vigilanceChezMoi : t.vigilancePrestataire}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "vous") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.vousTitre} aide={t.vousAide}>
                {identiteDe(false)}
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("civilite", "prenoms", "nom", "dateDeNaissance", "villeDeNaissance") ||
                        Boolean(refusDe("dateDeNaissance"))
                    }
                    onClick={() => aller("hote")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "hote") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.hoteTitre} aide={t.hoteAide}>
                {identiteDe(true)}
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("hoteCivilite", "hotePrenoms", "hoteNom", "hoteDateDeNaissance", "hoteVilleDeNaissance") ||
                        Boolean(refusDe("hoteDateDeNaissance"))
                    }
                    onClick={() => aller("adresse")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "adresse") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.adresseTitre} aide={t.adresseAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champVoie} valeur={r.voie} poser={poser("voie")} />
                    <Champ
                        libelle={t.champComplement}
                        aide={t.aideComplement}
                        valeur={r.complement}
                        poser={poser("complement")}
                    />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ
                            libelle={t.champCodePostal}
                            valeur={r.codePostal}
                            poser={poser("codePostal")}
                            erreur={erreurDe("codePostal")}
                        />
                        <div className="sm:col-span-2">
                            <Champ
                                libelle={t.champCommune}
                                valeur={r.commune}
                                poser={poser("commune")}
                            />
                        </div>
                    </div>
                    <Champ
                        type="date"
                        libelle={t.champDepuisLe}
                        aide={t.aideDepuisLe}
                        valeur={r.depuisLe}
                        poser={poser("depuisLe")}
                        erreur={erreurDe("depuisLe")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("voie", "codePostal", "commune", "depuisLe") ||
                        Boolean(refusDe("depuisLe")) ||
                        Boolean(refusDe("codePostal"))
                    }
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={2}
                onRetour={retour}
                titre={t.signatureTitre}
                aide={t.signatureAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureHote}
                        aide={t.signatureHoteAide}
                        effacer={t.effacer}
                        valeur={signature}
                        poser={setSignature}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li
                                key={cle}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {motifLisible(motif)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("memePersonne") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {t.alerteMemePersonne}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    {t.docEnFrancais && (
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-3">
                            {t.docEnFrancais}
                        </p>
                    )}

                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon
                                name={copie ? "check-circle-linear" : "copy-linear"}
                                className="text-lg"
                            />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.suiteTitre}
                            </div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, t.suite2, t.suite3].map((ligne) => (
                                <li
                                    key={ligne}
                                    className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                >
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="shield-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.fondTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {t.fond}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
